import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import PricingSection from '@/components/ui/pricing-section-4';
import UsagePanel from '@/components/Billing/UsagePanel';
import { CancellationInterviewModal } from '@/components/Billing/CancellationInterviewModal';
import { useBilling } from '@/hooks/useBilling';
import { useToast } from '@/hooks/useToast';
import { getApiClient, getAppBridge } from '@/api/client';
import { getShopId } from '@/utils/shopId';
import { parseShopContext } from '@/utils/shopContext';
import { redirectToUrl } from '@/utils/redirect';
import { toErrorMessage } from '@/utils/errorMessage';

/**
 * Query params Shopify (or our own return URLs) can land on /billing with.
 *
 * CRITICAL: none of these prove a payment happened. Shopify sends the merchant
 * to the SAME returnUrl whether the charge was approved OR declined/cancelled,
 * and a merchant can simply type `?extra_pack=success` into the address bar.
 * The only source of truth is `GET /api/billing/shopify/confirm`, which asks
 * Shopify what is genuinely active via `currentAppInstallation`.
 */
const BILLING_RETURN_PARAMS = [
  'confirmed',
  'extra_pack',
  'premium_upgrade',
  'charge_id',
  'subscription_id',
] as const;

const DECLINED_MESSAGE =
  'Your charge was declined or cancelled — your plan was not changed.';

const UNVERIFIABLE_MESSAGE =
  'We could not verify your payment with Shopify. Your plan was not changed — please refresh or contact support.';

interface ConfirmResponse {
  success?: boolean;
  message?: string;
}

function hasBillingReturnParams(params: URLSearchParams): boolean {
  return (
    params.get('confirmed') === 'true' ||
    params.get('extra_pack') === 'success' ||
    params.get('premium_upgrade') === 'success' ||
    !!params.get('charge_id')
  );
}

function messageOr(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

/**
 * Where cancellation actually happens.
 *
 * We are on the Shopify Billing API (see MONETIZATION_STRATEGY_HORMOZI.md
 * §10), so an app subscription is cancelled by the merchant from the Shopify
 * admin — this app has no in-app "cancel" endpoint and must not pretend to.
 * Returns null when we cannot name the store, in which case the interstitial
 * simply closes rather than sending the merchant to a broken URL.
 */
function shopifyAppSubscriptionsUrl(): string | null {
  const { shop } = parseShopContext();
  if (!shop) return null;
  const handle = shop.replace(/\.myshopify\.com$/i, '');
  if (!handle) return null;
  return `https://admin.shopify.com/store/${handle}/settings/apps`;
}

function BillingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { plan, loading: planLoading, refetch: refetchBilling } = useBilling();
  const { showSuccess, showError } = useToast();
  const [verifying, setVerifying] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);

  // Gate on `loading` as well as the plan itself. Without it the page renders
  // "Choose your plan" for a beat before the fetch resolves, so every existing
  // subscriber gets told to subscribe on each visit — a small flicker that
  // reads as the app forgetting they pay.
  const isSubscriber =
    !planLoading &&
    !!plan?.plan &&
    (plan.subscription?.status === 'active' || plan.subscription?.status === 'trialing');

  // Keep the latest callbacks/values in refs so the reconcile effect can depend
  // ONLY on the query string. `refetch` from BillingContext changes identity on
  // every plan update, which would otherwise re-run verification in a loop.
  const refetchRef = useRef(refetchBilling);
  refetchRef.current = refetchBilling;
  const showSuccessRef = useRef(showSuccess);
  showSuccessRef.current = showSuccess;
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Guards the reconcile from firing twice for the same URL. React 18
  // StrictMode double-invokes effects in dev; a second confirm call would
  // double-toast. Refs survive the StrictMode remount, the request does not
  // get cancelled, so the single in-flight verification still reports.
  const reconciledSearchRef = useRef<string | null>(null);

  /** Remove the billing params so a refresh cannot replay the flow. */
  const stripBillingParams = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    let changed = false;
    for (const key of BILLING_RETURN_PARAMS) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const query = next.toString();
    navigate(query ? `${pathnameRef.current}?${query}` : pathnameRef.current, {
      replace: true,
    });
  }, [navigate]);

  const stripBillingParamsRef = useRef(stripBillingParams);
  stripBillingParamsRef.current = stripBillingParams;

  /**
   * Reconcile a Shopify billing return against the server. NEVER branch on the
   * URL — branch on the backend's `success` field.
   */
  useEffect(() => {
    const search = searchParams.toString();
    if (!hasBillingReturnParams(searchParams)) return;
    if (reconciledSearchRef.current === search) return;
    reconciledSearchRef.current = search;

    // `charge_id` is only a lookup hint for one-time purchases; the backend
    // still requires Shopify to report the purchase as active before granting.
    const chargeId = searchParams.get('charge_id');
    const shopId = getShopId();

    const reconcile = async () => {
      setVerifying(true);
      try {
        const client = getApiClient();
        const query = new URLSearchParams();
        if (chargeId) query.set('charge_id', chargeId);
        if (shopId) query.set('shop_id', shopId);
        const qs = query.toString();

        const { data } = await client.get<ConfirmResponse>(
          qs ? `/api/billing/shopify/confirm?${qs}` : '/api/billing/shopify/confirm',
        );

        const verified = !!data && typeof data === 'object' && data.success === true;

        if (verified) {
          showSuccessRef.current(
            messageOr(data.message, 'Payment confirmed. Your plan is now active.'),
          );
        } else {
          showErrorRef.current(messageOr(data?.message, DECLINED_MESSAGE));
        }
      } catch (error: unknown) {
        console.error('Failed to verify Shopify billing state:', error);
        // toErrorMessage guarantees a string — rendering a raw error object in
        // the toast crashes React with minified error #31.
        showErrorRef.current(toErrorMessage(error, UNVERIFIABLE_MESSAGE));
      } finally {
        // Always refresh the real plan state and clear the params, even on
        // failure, so the page can never stay stuck on the verifying banner or
        // replay the flow on refresh.
        try {
          await refetchRef.current();
        } catch (refetchError: unknown) {
          console.error('Failed to refresh billing state:', refetchError);
        }
        if (mountedRef.current) {
          setVerifying(false);
          stripBillingParamsRef.current();
        }
      }
    };

    void reconcile();
  }, [searchParams]);

  /**
   * Close the interview and hand the merchant to Shopify to finish cancelling.
   *
   * Runs unconditionally — the feedback POST was already fired and abandoned
   * inside the modal, so nothing here can be blocked by it. If we cannot build
   * the admin URL we still close rather than showing an error: the merchant
   * can always cancel from the Shopify admin themselves, and an error at this
   * point reads as "your cancellation failed".
   */
  const handleProceedToCancel = useCallback(() => {
    setInterviewOpen(false);
    const url = shopifyAppSubscriptionsUrl();
    if (!url) return;
    redirectToUrl(url, getAppBridge());
  }, []);

  return (
    <div className="space-y-6">
      {verifying && (
        <div className="glass-panel flex items-center gap-3 text-sm text-slate-600">
          <span className="h-2 w-2 animate-ping rounded-full bg-indigo-400" />
          Verifying your payment with Shopify...
        </div>
      )}

      {isSubscriber ? (
        <>
          <UsagePanel plan={plan!} />

          <div className="glass-panel">
            <div className="flex flex-col gap-3">
              <span className="ghost-chip">Plans</span>
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  Need more capacity?
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Upgrade for a higher monthly allowance, or top up this period with an
                  extra pack.
                </p>
              </div>
              {/* Cancel intent. Rendered only for actual subscribers — there is
                  nothing to cancel otherwise. Deliberately understated: this is
                  a way out, not a call to action. */}
              <div className="border-t border-slate-100 pt-3">
                <button
                  onClick={() => setInterviewOpen(true)}
                  className="text-sm text-slate-400 underline transition hover:text-slate-600"
                >
                  Cancel my subscription
                </button>
              </div>
            </div>
          </div>

          <CancellationInterviewModal
            isOpen={interviewOpen}
            onClose={() => setInterviewOpen(false)}
            onProceed={handleProceedToCancel}
          />
        </>
      ) : (
        <div className="glass-panel">
          <div className="flex flex-col gap-3">
            <span className="ghost-chip">Billing</span>
            <div>
              <h1 className="text-3xl font-semibold text-slate-900">Choose your plan</h1>
              <p className="mt-2 text-sm text-slate-500">
                Your first AR-ready product is free — yours to keep, no time limit. Then roll 3D &amp; AR out across your catalog.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_35px_120px_rgba(15,23,42,0.12)]">
        <PricingSection />
      </div>
    </div>
  );
}

export default BillingPage;
export { BillingPage };
